/**
 * "Last checked" for a retailer offer: how long since the poller last asked.
 *
 * Best Buy's stock.last_checked is a LocalDateTime written as UTC wall time and
 * serialised without a zone ("2026-09-07T15:58:40.181243"); Micro Center and
 * Newegg timestamps are Instants with a trailing Z. A naive string is read as
 * UTC, otherwise every US viewer would see it 4-7 hours in the future.
 */
export const POLL_SECONDS = {
    // Best Buy runs every minute, but each product comes round about every four
    // (p50 140s, max 218s measured 2026-09-07); stale = twice that.
    bestBuy: 240,
    microCenter: 15 * 60,
    newegg: 15 * 60
};

export const parseChecked = (value) => {
    if (!value) {
        return null;
    }
    let date;
    if (typeof value === 'number') {
        date = new Date(value < 1_000_000_000_000 ? value * 1000 : value);
    } else if (typeof value === 'string') {
        const hasZone = /(Z|[+-]\d{2}:?\d{2})$/i.test(value);
        date = new Date(hasZone ? value : `${value}Z`);
    } else {
        date = new Date(value);
    }
    return Number.isNaN(date.getTime()) ? null : date;
};

/** Seconds since the check, never negative (clock skew between the poller and the viewer). */
export const secondsSince = (value, now = Date.now()) => {
    const date = parseChecked(value);
    if (!date) {
        return null;
    }
    return Math.max(0, Math.floor((now - date.getTime()) / 1000));
};

/** "41 seconds ago", "6 minutes ago", "3 hours ago", "2 days ago"; short form for cards. */
export const formatAgo = (seconds, { short = false } = {}) => {
    if (seconds === null || seconds === undefined) {
        return null;
    }
    const unit = (n, s, l) => (short ? `${n}${s}` : `${n} ${l}${n === 1 ? '' : 's'}`);
    if (seconds < 60) {
        return `${unit(seconds, 's', 'second')} ago`;
    }
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) {
        return `${unit(minutes, 'm', 'minute')} ago`;
    }
    const hours = Math.floor(minutes / 60);
    if (hours < 48) {
        return `${unit(hours, 'h', 'hour')} ago`;
    }
    return `${unit(Math.floor(hours / 24), 'd', 'day')} ago`;
};

/** A check older than twice its cadence is stale: a stalled poller must look stalled, not fresh. */
export const isStale = (seconds, pollSeconds) => seconds !== null && seconds !== undefined && seconds > pollSeconds * 2;

export const describeChecked = (value, pollSeconds, { now = Date.now(), short = false } = {}) => {
    const seconds = secondsSince(value, now);
    if (seconds === null) {
        return { text: null, stale: false, seconds: null };
    }
    return { text: formatAgo(seconds, { short }), stale: isStale(seconds, pollSeconds), seconds };
};

/** The most recent check across every retailer attached to a listing row (#283). */
export const freshestCheck = (stock) => {
    const candidates = [
        { key: 'bestBuy', label: 'Best Buy', value: stock?.bestBuyLastChecked, poll: POLL_SECONDS.bestBuy },
        { key: 'microCenter', label: 'Micro Center', value: stock?.mcLastChecked ?? stock?.mcOffer?.lastChecked, poll: POLL_SECONDS.microCenter },
        ...((stock?.retailerOffers || [])
            .filter((offer) => offer?.retailer === 'NEWEGG')
            .map((offer) => ({ key: 'newegg', label: 'Newegg', value: offer.lastChecked, poll: POLL_SECONDS.newegg })))
    ]
        .map((c) => ({ ...c, date: parseChecked(c.value) }))
        .filter((c) => c.date);
    if (candidates.length === 0) {
        return null;
    }
    candidates.sort((a, b) => b.date - a.date);
    return { ...candidates[0], all: candidates };
};
